/**
 * Level Filter - Multi-checkbox selector for log levels
 */

import React, { useState, useCallback, useRef, useEffect } from "react";

export interface LevelFilterProps {
  levels: string[];
  selectedLevels: Set<string>;
  onSelectionChange: (selected: Set<string>) => void;
  allSelected: boolean;
  onAllSelectedChange: (allSelected: boolean) => void;
}

export function LevelFilter(props: LevelFilterProps) {
  const { levels, selectedLevels, onSelectionChange, allSelected, onAllSelectedChange } = props;
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleToggle = useCallback((level: string) => {
    const newSelected = new Set(selectedLevels);
    if (newSelected.has(level)) {
      newSelected.delete(level);
    } else {
      newSelected.add(level);
    }
    onSelectionChange(newSelected);
    // If we deselected something, clear the "all selected" flag
    if (newSelected.has(level)) {
      // We just added it, check if we now have everything
      if (newSelected.size === levels.length) {
        onAllSelectedChange(true);
      }
    } else {
      // We just removed it, clear the flag
      onAllSelectedChange(false);
    }
  }, [selectedLevels, onSelectionChange, onAllSelectedChange, levels]);

  const handleSelectAll = useCallback(() => {
    onSelectionChange(new Set(levels));
    onAllSelectedChange(true);
  }, [levels, onSelectionChange, onAllSelectedChange]);

  const handleClearAll = useCallback(() => {
    onSelectionChange(new Set());
    onAllSelectedChange(false);
  }, [onSelectionChange, onAllSelectedChange]);

  const selectedCount = selectedLevels.size;
  const totalCount = levels.length;
  const buttonLabel = allSelected
    ? "Levels: All"
    : selectedCount === 0
    ? "Levels: None"
    : `Levels: ${selectedCount}/${totalCount}`;

  // Create tooltip showing selected values
  const tooltipText = allSelected
    ? "All levels selected"
    : selectedCount === 0
    ? "No levels selected"
    : `Selected: ${Array.from(selectedLevels).sort().join(', ')}`;

  if (levels.length === 0) {
    return null; // Don't show if no levels
  }

  return (
    <div className="level-filter" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="level-filter__button"
        title={tooltipText}
      >
        {buttonLabel}
      </button>

      {isOpen && (
        <>
          <div className="level-filter__backdrop" onClick={() => setIsOpen(false)} />
          <div className="level-filter__menu">
            <div className="level-filter__header">
              <span>Log Levels</span>
              <div className="level-filter__actions">
                <button onClick={handleSelectAll} className="level-filter__action">
                  All
                </button>
                <button onClick={handleClearAll} className="level-filter__action">
                  None
                </button>
              </div>
            </div>
            <div className="level-filter__items">
              {levels.map(level => (
                <label key={level} className="level-filter__item">
                  <input
                    type="checkbox"
                    checked={selectedLevels.has(level)}
                    onChange={() => handleToggle(level)}
                  />
                  <span>{level}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
